/**
 * Beleg importieren — das Papier zuerst, die Zahlen danach.
 *
 * Vorher fragte dieser Dialog Nummer, Kunde und Betrag ab und bot die
 * Belegdatei ganz unten als graues „Datei auswählen" hinter der Bruttosumme an.
 * Technisch funktionierte das; in der Praxis rutschte die Datei durch, und der
 * Beleg musste im zweiten Schritt aus dem Register nachgereicht werden. Ein
 * Import, bei dem das Dokument der letzte Schritt ist, ist falsch herum gebaut.
 *
 * Jetzt beginnt der Dialog mit dem Beleg. Die KI liest ihn — Kopf, Anschrift,
 * Texte und jede Position — und legt einen Web-Entwurf an, den der Editor
 * öffnet. Ab da ist es ein Kontor-Dokument wie jedes andere: bearbeitbar, mit
 * Vorschau, mit Folgebelegen.
 *
 * Der alte Weg bleibt darunter als „nur als Registerzeile erfassen" — für
 * Altbestand, von dem es kein Papier mehr gibt. Er ist bewusst zugeklappt:
 * eine Zeile ohne Dokument ist die Ausnahme, nicht der Normalfall.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { Kunde } from "../../lib/types";
import {
  errorText,
  euro,
  KIND_LABEL,
  type InvoiceCreate,
  type InvoiceCreated,
  type InvoiceImportResult,
  type InvoiceKind,
  type InvoiceStatus,
  type ProjektWirkung,
} from "../../lib/invoices";
import {
  useCreateInvoiceRecord,
  useImportInvoiceKi,
  useUploadInvoicePdf,
} from "../../hooks/useInvoices";
import { useKundeProjekte } from "../../hooks/usePlanung";

type Props = {
  initialKind: InvoiceKind;
  customers: Kunde[];
  onClose: () => void;
  /** Registerzeile angelegt — die Seite öffnet den Beleg im Drawer. */
  onCreated: (item: InvoiceCreated) => void;
  /** Beleg eingelesen — die Seite öffnet den Entwurf im Editor. */
  onImported: (draftId: string) => void;
};

const localIso = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const plusDays = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localIso(date);
};

const ERLAUBTE_TYPEN = ".pdf,.jpg,.jpeg,.png";
const MAX_BYTES = 15 * 1024 * 1024;

const dateiGroesse = (bytes: number) => (
  bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
);

export default function InvoiceCreateDialog({
  initialKind, customers, onClose, onCreated, onImported,
}: Props) {
  const [manuellOffen, setManuellOffen] = useState(false);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="invoice-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="invoice-modal" role="dialog" aria-modal="true" aria-labelledby="invoice-create-title">
        <header className="invoice-modal-head">
          <div>
            <p className="invoice-kicker">Import / Altbestand</p>
            <h2 id="invoice-create-title">Beleg importieren</h2>
          </div>
          <button className="invoice-icon-button" type="button" onClick={onClose} aria-label="Schließen">×</button>
        </header>

        <KiImport onImported={onImported} />

        <div className="invoice-fallback">
          <button
            type="button"
            className="invoice-fallback-toggle"
            aria-expanded={manuellOffen}
            onClick={() => setManuellOffen((offen) => !offen)}
          >
            <span>{manuellOffen ? "▾" : "▸"} Nur als Registerzeile erfassen</span>
            <small>Für Altbestand ohne Dokument — Nummer, Kunde und Betrag von Hand.</small>
          </button>
          {manuellOffen && (
            <RegisterzeileForm
              initialKind={initialKind}
              customers={customers}
              onClose={onClose}
              onCreated={onCreated}
            />
          )}
        </div>
      </section>
    </div>
  );
}

/**
 * Stufe eins: das Papier.
 *
 * Der Vision-Aufruf läuft bis zu anderthalb Minuten. Deshalb sagt die Fläche
 * währenddessen, was gerade passiert — ein stiller Spinner lädt zum zweiten
 * Klick ein, und der zweite Klick wäre ein zweiter Entwurf.
 */
function KiImport({ onImported }: { onImported: (draftId: string) => void }) {
  const importieren = useImportInvoiceKi();
  const [datei, setDatei] = useState<File | null>(null);
  const [validation, setValidation] = useState("");
  const [ergebnis, setErgebnis] = useState<InvoiceImportResult | null>(null);
  const [ueberZone, setUeberZone] = useState(false);
  const dateiFeld = useRef<HTMLInputElement>(null);

  const waehlen = (liste: FileList | null) => {
    const gewaehlt = liste?.[0] || null;
    if (gewaehlt && gewaehlt.size > MAX_BYTES) {
      setValidation("Die Datei ist größer als 15 MB.");
      return;
    }
    setValidation("");
    setErgebnis(null);
    setDatei(gewaehlt);
  };

  const einlesen = async () => {
    if (!datei) return;
    setValidation("");
    try {
      setErgebnis(await importieren.mutateAsync(datei));
    } catch (error) {
      setValidation(errorText(error));
    }
  };

  const zuruecksetzen = () => {
    setDatei(null);
    setErgebnis(null);
    setValidation("");
    if (dateiFeld.current) dateiFeld.current.value = "";
  };

  if (ergebnis) {
    return <ImportErgebnis ergebnis={ergebnis} onOeffnen={() => onImported(ergebnis.id)} onWeiter={zuruecksetzen} />;
  }

  return (
    <div className="invoice-import">
      <div
        className={`invoice-dropzone${ueberZone ? " over" : ""}${importieren.isPending ? " busy" : ""}`}
        onDragOver={(event) => { event.preventDefault(); setUeberZone(true); }}
        onDragLeave={() => setUeberZone(false)}
        onDrop={(event) => {
          event.preventDefault();
          setUeberZone(false);
          if (!importieren.isPending) waehlen(event.dataTransfer.files);
        }}
      >
        {importieren.isPending ? (
          <>
            <strong>Clara liest den Beleg …</strong>
            <small>
              Kopf, Anschrift und jede Position — das dauert bis zu anderthalb Minuten.
              Bitte das Fenster offen lassen.
            </small>
          </>
        ) : datei ? (
          <>
            <strong>{datei.name}</strong>
            <small>{dateiGroesse(datei.size)}</small>
            <div className="invoice-dropzone-actions">
              <button className="invoice-button primary" type="button" onClick={einlesen}>Beleg einlesen</button>
              <button className="invoice-button" type="button" onClick={zuruecksetzen}>andere Datei</button>
            </div>
          </>
        ) : (
          <>
            <strong>Angebot oder Rechnung hierher ziehen</strong>
            <small>PDF, JPG oder PNG bis 15 MB. Clara liest die Positionen und legt einen Entwurf an.</small>
            <button className="invoice-button" type="button" onClick={() => dateiFeld.current?.click()}>Datei wählen</button>
          </>
        )}
        <input
          ref={dateiFeld}
          type="file"
          accept={ERLAUBTE_TYPEN}
          hidden
          onChange={(event) => waehlen(event.target.files)}
        />
      </div>
      {validation && <p className="invoice-form-error" role="alert">{validation}</p>}
    </div>
  );
}

/**
 * Was gelesen wurde — bevor irgendetwas gespeichert weitergeht.
 *
 * Die Warnungen stehen oben und nicht als Fußnote. Eine falsch gelesene Menge
 * ergibt ein Dokument, das völlig plausibel aussieht und trotzdem den falschen
 * Preis trägt; wer das erst beim Kunden merkt, hat einen teuren Fehler.
 */
function ImportErgebnis({
  ergebnis, onOeffnen, onWeiter,
}: {
  ergebnis: InvoiceImportResult;
  onOeffnen: () => void;
  onWeiter: () => void;
}) {
  const positionen = (ergebnis.document.line_rows || []).filter((zeile) => zeile.line_kind === "position");
  const nummerHinweis = ergebnis.nummer_status?.hinweis || "";
  const nummerHart = Boolean(ergebnis.nummer_status?.belegt && !ergebnis.nummer_status?.ersetzbar);

  return (
    <div className="invoice-result">
      <p className="invoice-result-head">
        ✓ <strong>{positionen.length} {positionen.length === 1 ? "Position" : "Positionen"}</strong> aus{" "}
        {ergebnis.seiten} {ergebnis.seiten === 1 ? "Seite" : "Seiten"} gelesen.
      </p>

      {ergebnis.warnungen.length > 0 && (
        <ul className="invoice-result-warnungen" role="alert">
          {ergebnis.warnungen.map((warnung) => <li key={warnung}>{warnung}</li>)}
        </ul>
      )}
      {nummerHinweis && (
        <p className={nummerHart ? "invoice-form-error" : "invoice-result-note"} role={nummerHart ? "alert" : undefined}>
          {nummerHinweis}
        </p>
      )}

      <dl className="invoice-result-facts">
        <div><dt>Belegart</dt><dd>{KIND_LABEL[ergebnis.document.kind]}</dd></div>
        <div><dt>Nummer</dt><dd>{ergebnis.nummer || "— wird beim Festschreiben vergeben"}</dd></div>
        <div><dt>Kunde</dt><dd>{ergebnis.kunde_name || "— bitte im Editor wählen"}</dd></div>
        <div><dt>Projekt</dt><dd>{ergebnis.projekt_title || "— keines zugeordnet"}</dd></div>
        <div><dt>Datum</dt><dd>{ergebnis.document.issue_date}</dd></div>
        <div><dt>Netto</dt><dd>{euro(ergebnis.netto)}</dd></div>
        <div><dt>Brutto</dt><dd><strong>{euro(ergebnis.brutto)}</strong></dd></div>
      </dl>

      <ol className="invoice-result-positions">
        {positionen.map((zeile, index) => (
          <li key={`${zeile.title}-${index}`}>
            <span>{zeile.title || "ohne Bezeichnung"}</span>
            <small>{zeile.quantity} {zeile.unit_label} × {euro(zeile.unit_price_net)}</small>
          </li>
        ))}
        {!positionen.length && <li className="invoice-picklist-empty">Keine Position erkannt.</li>}
      </ol>

      <footer className="invoice-modal-actions">
        <button className="invoice-button" type="button" onClick={onWeiter}>Noch einen einlesen</button>
        <button className="invoice-button primary" type="button" onClick={onOeffnen}>Im Editor öffnen</button>
      </footer>
    </div>
  );
}

/**
 * Der alte Weg: nur Zahlen ins Register, Datei optional.
 *
 * Bleibt für Altbestand, von dem kein Dokument mehr existiert. Kunde und
 * Projekt kommen aus angeklickten Trefferlisten statt aus `<datalist>`-Feldern —
 * die verlangten einen exakten Treffer auf einen zusammengesetzten Text, und
 * wer tippte statt zu klicken, kam nicht weiter.
 */
function RegisterzeileForm({
  initialKind, customers, onClose, onCreated,
}: {
  initialKind: InvoiceKind;
  customers: Kunde[];
  onClose: () => void;
  onCreated: (item: InvoiceCreated) => void;
}) {
  const createRecord = useCreateInvoiceRecord();
  const uploadPdf = useUploadInvoicePdf();
  const [kind, setKind] = useState<InvoiceKind>(initialKind);
  const [status, setStatus] = useState<InvoiceStatus>("entwurf");
  const [nummer, setNummer] = useState("");
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [issueDate, setIssueDate] = useState(localIso());
  const [dueDate, setDueDate] = useState(plusDays(14));
  const [net, setNet] = useState("0.00");
  const [vatRate, setVatRate] = useState(19);
  const [note, setNote] = useState("");
  const [datei, setDatei] = useState<File | null>(null);
  const [validation, setValidation] = useState("");
  const [ergebnis, setErgebnis] = useState<{
    item: InvoiceCreated;
    dateiName: string;
    dateiFehler: string;
    wirkung: ProjektWirkung | null;
  } | null>(null);
  const dateiFeld = useRef<HTMLInputElement>(null);

  const kundeProjekte = useKundeProjekte(customerId || undefined);
  const gewaehlterKunde = customers.find((c) => c.id === customerId) || null;

  const netNumber = Number(net) || 0;
  const gross = useMemo(() => Math.round(netNumber * (1 + vatRate / 100) * 100) / 100, [netNumber, vatRate]);

  const treffer = useMemo(() => {
    const query = customerQuery.trim().toLowerCase();
    if (!query) return customers.slice(0, 8);
    return customers
      .filter((c) => `${c.name} ${c.ort || ""} ${c.email || ""}`.toLowerCase().includes(query))
      .slice(0, 8);
  }, [customers, customerQuery]);

  const dateiWaehlen = (liste: FileList | null) => {
    const gewaehlt = liste?.[0] || null;
    if (gewaehlt && gewaehlt.size > MAX_BYTES) {
      setValidation("Die Datei ist größer als 15 MB.");
      return;
    }
    setValidation("");
    setDatei(gewaehlt);
  };

  const zuruecksetzen = () => {
    setNummer("");
    setNet("0.00");
    setNote("");
    setDatei(null);
    setProjectId("");
    setErgebnis(null);
    if (dateiFeld.current) dateiFeld.current.value = "";
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setValidation("");
    if (!nummer.trim()) return setValidation("Bitte eine Belegnummer eintragen.");
    if (!customerId) return setValidation("Bitte einen Kunden aus der Trefferliste auswählen.");
    const payload: InvoiceCreate = {
      nummer: nummer.trim(), kind, status, kunde_id: customerId, projekt_id: projectId,
      issue_date: issueDate, faellig_am: dueDate || null, netto: netNumber, brutto: gross, notiz: note,
    };
    let item: InvoiceCreated;
    try {
      item = await createRecord.mutateAsync(payload);
    } catch {
      return; /* Fehlermeldung kommt aus dem Mutation-State. */
    }
    // Der Eintrag steht — ein Fehler beim Anhang darf ihn nicht verschlucken.
    // Deshalb wird er gemeldet statt geworfen; nachreichen geht im Beleg.
    let dateiFehler = "";
    if (datei) {
      try {
        await uploadPdf.mutateAsync({ id: item.id, datei });
      } catch (error) {
        dateiFehler = errorText(error);
      }
    }
    setErgebnis({
      item,
      dateiName: datei && !dateiFehler ? datei.name : "",
      dateiFehler,
      wirkung: item.projekt_wirkung || null,
    });
  };

  const message = validation || (createRecord.error ? errorText(createRecord.error) : "");
  const laeuft = createRecord.isPending || uploadPdf.isPending;

  if (ergebnis) {
    return (
      <Ergebnis
        item={ergebnis.item}
        dateiName={ergebnis.dateiName}
        dateiFehler={ergebnis.dateiFehler}
        wirkung={ergebnis.wirkung}
        onOeffnen={() => onCreated(ergebnis.item)}
        onWeiter={zuruecksetzen}
      />
    );
  }

  return (
    <form onSubmit={submit}>
      <div className="invoice-kind-picker" aria-label="Belegart">
        {(["invoice", "quote", "partial_invoice", "final_invoice"] as InvoiceKind[]).map((value) => (
          <button key={value} type="button" className={kind === value ? "active" : ""} onClick={() => setKind(value)}>
            {KIND_LABEL[value]}
          </button>
        ))}
      </div>

      <div className="invoice-form-grid">
        <label className="invoice-field">
          <span>Vorhandene Belegnummer *</span>
          <input autoFocus value={nummer} onChange={(event) => setNummer(event.target.value)} placeholder={kind === "quote" ? "A-2026-…" : "R-2026-…"} />
          <small>Die Nummer aus dem gemeinsamen Nummernkreis verwenden.</small>
        </label>
        <label className="invoice-field">
          <span>Status</span>
          <div className="invoice-mini-segments">
            {(["entwurf", "gestellt"] as InvoiceStatus[]).map((value) => (
              <button key={value} type="button" className={status === value ? "active" : ""} onClick={() => setStatus(value)}>
                {value === "entwurf" ? "Entwurf" : "Gestellt"}
              </button>
            ))}
          </div>
        </label>

        <div className="invoice-field invoice-field-wide">
          <span>Kunde *</span>
          {gewaehlterKunde ? (
            <div className="invoice-picked">
              <strong>{gewaehlterKunde.name}</strong>
              {gewaehlterKunde.ort && <small>{gewaehlterKunde.ort}</small>}
              <button
                type="button"
                className="invoice-picked-change"
                onClick={() => { setCustomerId(""); setProjectId(""); setCustomerQuery(""); }}
              >
                ändern
              </button>
            </div>
          ) : (
            <>
              <input
                value={customerQuery}
                onChange={(event) => setCustomerQuery(event.target.value)}
                placeholder="Kunde suchen …"
              />
              <ul className="invoice-picklist">
                {treffer.map((customer) => (
                  <li key={customer.id}>
                    <button type="button" onClick={() => { setCustomerId(customer.id); setProjectId(""); }}>
                      <strong>{customer.name}</strong>
                      <small>{[customer.ort, customer.email].filter(Boolean).join(" · ") || "keine weiteren Angaben"}</small>
                    </button>
                  </li>
                ))}
                {!treffer.length && <li className="invoice-picklist-empty">Kein Kunde gefunden.</li>}
              </ul>
            </>
          )}
        </div>

        <div className="invoice-field invoice-field-wide">
          <span>Projekt</span>
          {!customerId ? (
            <small>Erst den Kunden wählen — danach stehen seine Projekte hier.</small>
          ) : kundeProjekte.isLoading ? (
            <small>Projekte werden geladen …</small>
          ) : kundeProjekte.data?.length ? (
            <>
              <div className="invoice-chip-row">
                <button type="button" className={projectId ? "" : "active"} onClick={() => setProjectId("")}>
                  Ohne Projekt
                </button>
                {kundeProjekte.data.map((projekt) => (
                  <button
                    key={projekt.id}
                    type="button"
                    className={projectId === projekt.id ? "active" : ""}
                    onClick={() => setProjectId(projekt.id)}
                  >
                    {projekt.title || "ohne Titel"}
                    {projekt.status && <em>{projekt.status}</em>}
                  </button>
                ))}
              </div>
              <small>
                {kind === "quote"
                  ? "Ein zugeordnetes Angebot setzt den Auftragswert des Projekts."
                  : "Zugeordnete Rechnungen bestimmen den Auftragswert des Projekts."}
              </small>
            </>
          ) : (
            <small>Für diesen Kunden ist kein Projekt angelegt.</small>
          )}
        </div>

        <label className="invoice-field">
          <span>Belegdatum</span>
          <input type="date" value={issueDate} onChange={(event) => setIssueDate(event.target.value)} />
        </label>
        <label className="invoice-field">
          <span>{kind === "quote" ? "Gültig bis" : "Fällig am"}</span>
          <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
        </label>
        <label className="invoice-field">
          <span>Netto</span>
          <div className="invoice-money-input"><input type="number" min="0" step="0.01" value={net} onChange={(event) => setNet(event.target.value)} /><b>€</b></div>
        </label>
        <label className="invoice-field">
          <span>Umsatzsteuer</span>
          <select value={vatRate} onChange={(event) => setVatRate(Number(event.target.value))}>
            <option value={19}>19 %</option><option value={7}>7 %</option><option value={0}>0 %</option>
          </select>
        </label>
        <div className="invoice-total-preview invoice-field-wide">
          <span>Bruttosumme</span><strong>{gross.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</strong>
        </div>

        <div className="invoice-field invoice-field-wide">
          <span>Belegdatei</span>
          <div className="invoice-file-row">
            <input
              ref={dateiFeld}
              type="file"
              accept={ERLAUBTE_TYPEN}
              onChange={(event) => dateiWaehlen(event.target.files)}
            />
            {datei && (
              <button
                type="button"
                className="invoice-picked-change"
                onClick={() => { setDatei(null); if (dateiFeld.current) dateiFeld.current.value = ""; }}
              >
                entfernen
              </button>
            )}
          </div>
          <small>
            {datei
              ? `${datei.name} · ${dateiGroesse(datei.size)}`
              : "PDF, JPG oder PNG bis 15 MB. Ohne Datei bleibt der Eintrag eine reine Registerzeile."}
          </small>
        </div>

        <label className="invoice-field invoice-field-wide">
          <span>Interne Notiz</span>
          <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Optionaler Hinweis zum Registereintrag" />
        </label>
      </div>

      {message && <p className="invoice-form-error" role="alert">{message}</p>}
      <footer className="invoice-modal-actions">
        <button className="invoice-button" type="button" onClick={onClose}>Abbrechen</button>
        <button className="invoice-button primary" type="submit" disabled={laeuft}>
          {createRecord.isPending ? "Wird gespeichert …" : uploadPdf.isPending ? "Datei wird abgelegt …" : "Im Register anlegen"}
        </button>
      </footer>
    </form>
  );
}

/**
 * Was der Registereintrag bewirkt hat — bevor der Dialog zugeht.
 *
 * Ohne diese Rückmeldung war nicht zu sehen, ob die Datei angekommen ist und
 * was der Beleg mit dem Auftragswert des Projekts gemacht hat. Beides
 * nachträglich zu prüfen hieße: zwei Seiten öffnen.
 */
function Ergebnis({
  item, dateiName, dateiFehler, wirkung, onOeffnen, onWeiter,
}: {
  item: InvoiceCreated;
  dateiName: string;
  dateiFehler: string;
  wirkung: ProjektWirkung | null;
  onOeffnen: () => void;
  onWeiter: () => void;
}) {
  return (
    <div className="invoice-result">
      <p className="invoice-result-head">✓ <strong>{item.nummer}</strong> liegt im Register.</p>
      <ul className="invoice-result-list">
        <li>
          {dateiName
            ? <>Belegdatei hinterlegt: <strong>{dateiName}</strong></>
            : dateiFehler
              ? <span className="invoice-result-warn">Die Datei ließ sich nicht ablegen: {dateiFehler} — im Beleg nachreichen.</span>
              : "Ohne Belegdatei — im Beleg lässt sie sich nachreichen."}
        </li>
        <li>
          {item.projekt_title
            ? <>Projekt: <strong>{item.projekt_title}</strong></>
            : "Keinem Projekt zugeordnet."}
        </li>
        {wirkung && (
          <li>
            {wirkung.geaendert
              ? <>Auftragswert <strong>{euro(wirkung.alt)} → {euro(wirkung.neu)}</strong> <small>({wirkung.herkunft_text})</small></>
              : <>Auftragswert unverändert bei <strong>{euro(wirkung.auftragswert)}</strong></>}
          </li>
        )}
      </ul>
      <footer className="invoice-modal-actions">
        <button className="invoice-button" type="button" onClick={onWeiter}>Noch einen erfassen</button>
        <button className="invoice-button primary" type="button" onClick={onOeffnen}>Beleg öffnen</button>
      </footer>
    </div>
  );
}
